import React from 'react';
import { Phone, AlertTriangle, CheckCircle, Thermometer, Droplet, Wind } from 'lucide-react';
export function AutoMythsDebunked() {
  return <div className="min-h-screen bg-gray-50" data-id="element-856">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-857">
        <div className="container mx-auto px-4" data-id="element-858">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-859">
            Seven Summertime Auto Myths Debunked
          </h1>
          <p className="text-xl text-gray-100" data-id="element-860">
            Separating Fact from Fiction for Summer Driving
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-861">
        <div className="max-w-4xl mx-auto" data-id="element-862">
          {/* Introduction */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-863">
            <p className="text-lg text-gray-700 mb-6" data-id="element-864">
              The added heat of summer driving places a strain on your car's
              cooling and electrical systems. Batteries don't live as long in
              high underhood temps, and the extra heat and pressure places a
              strain on hoses and belts. High speeds and hot pavement are also
              tough on tires and brakes.
            </p>

            <p className="text-lg text-gray-700 mb-6" data-id="element-865">
              These are all good reasons to give your vehicle a good checkup
              before leaving on that vacation road trip, starting with checking
              the tire pressures and all the fluids. But are there good ways to
              prevent overheating (not you—the car)? Do you really need
              antifreeze in your radiator all summer?
            </p>

            <p className="text-lg text-gray-700" data-id="element-866">
              Should you use heavier oil? There are plenty of old wives' tales,
              advice from distant relatives and just plain weird stuff you read
              on the Internet that confuse drivers. Here are some real facts
              about summertime and your car.
            </p>
          </div>

          {/* Myth 1 - Heat */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-867">
            <div className="flex items-start mb-4" data-id="element-868">
              <Thermometer className="text-[#B1050B] mr-3 flex-shrink-0 mt-1" size={28} data-id="element-869" />
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-870">
                1) "Does turning the heat on really help cool an engine?"
              </h2>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-4" data-id="element-871">
              <p className="text-gray-800 font-semibold mb-2" data-id="element-872">✓ TRUE</p>
              <p className="text-gray-700" data-id="element-873">
                Yes. The excess heat goes into the heater core, which is just a
                tiny radiator, pulling the temperature of the coolant down. Of
                course, it will rapidly overheat the interior of the car--and
                you.
              </p>
            </div>

            <p className="text-gray-700" data-id="element-874">
              If doing this means getting to the top of that hill without
              boiling over, it might get you to someplace that can check the
              cooling system for problems. If you need to use the heater
              frequently to reduce coolant temperatures, either there's
              something wrong with your cooling system, or you've seriously
              overloaded your car or truck.
            </p>
          </div>

          {/* Myth 2 - Oil */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-875">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-876">
              2) "Does the summertime require thicker oil?"
            </h2>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-4" data-id="element-877">
              <p className="text-gray-800 font-semibold mb-2" data-id="element-878">
                ⚠ PARTIALLY TRUE - But Don't Go Too Thick
              </p>
              <p className="text-gray-700" data-id="element-879">
                According to the car manufacturers, yes--but don't go too thick.
                Most new cars today are supposed to be filled with 5W-20, which
                is pretty thin. Car manufacturers usually list 10W-30 as
                acceptable for summer temperatures.
              </p>
            </div>

            <p className="text-gray-700 mb-4" data-id="element-880">
              Thinner oil has less friction inside the engine, which is good for
              fuel economy. Engines have been designed to run on this thin stuff
              for a number of years, with improved surface finish and
              low-friction parts like roller camshafts. Check your owner's
              manual for the viscosity recommendations for your vehicle.
            </p>

            <p className="text-gray-700 mb-4" data-id="element-881">
              Older cars may require thicker oil, but it's still not a good idea
              to just pour five quarts of 40-weight in just because it's summer.
              Thicker oil takes longer to start flowing properly when the engine
              isn't warmed up, and can also reduce fuel economy.
            </p>

            <p className="text-gray-700" data-id="element-882">
              If you live in Phoenix and tow a huge trailer, the temptation is
              to use thicker oil to keep it from thinning out excessively and
              damaging your engine. Unless you've got an older car, made in the
              era of 20W40 or even straight grades, don't just change your oil's
              viscosity up arbitrarily. Either switch to a synthetic, or install
              an aftermarket oil cooler, or both.
            </p>
          </div>

          {/* Myth 3 - Coolant */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-883">
            <div className="flex items-start mb-4" data-id="element-884">
              <Droplet className="text-[#B1050B] mr-3 flex-shrink-0 mt-1" size={28} data-id="element-885" />
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-886">
                3) "What kind of coolant or mix of coolant is appropriate for
                summer?"
              </h2>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-4" data-id="element-887">
              <p className="text-gray-800 font-semibold mb-2" data-id="element-888">
                ✗ FALSE - Uncle Chuck is Wrong
              </p>
              <p className="text-gray-700" data-id="element-889">
                Your Uncle Chuck says it's better to use just plain water during
                the summer. If that's what he's doing, he's running his car
                without any anticorrosion or antiwear additives in the cooling
                system, and he'll have problems.
              </p>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-6" data-id="element-890">
              <p className="text-gray-800 font-semibold mb-2" data-id="element-891">
                ✓ CORRECT ANSWER:
              </p>
              <p className="text-gray-700" data-id="element-892">
                Anytime, winter or summer, always use a 50-50 mix of fresh
                coolant and water. That's true regardless of the brand or type
                of coolant your car manufacturer recommends. Read the
                label--some coolant on store shelves is already pre-diluted to
                50-50.
              </p>
            </div>
          </div>

          {/* Myth 4 - Gas in Morning */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-893">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-894">
              4) Is it better to buy gasoline in the morning when the fuel is
              cooler? Later in the day, as the gas warms up and expands don't
              you get less gas for your money?
            </h2>

            <div className="bg-red-50 border-l-4 border-red-500 p-6" data-id="element-895">
              <p className="text-gray-800 font-semibold mb-2" data-id="element-896">✗ FALSE</p>
              <p className="text-gray-700" data-id="element-897">
                Wrong. The daily temperature shift in underground fuel tanks is
                negligible, because they're underground where the soil stays the
                same temp all the time.
              </p>
            </div>
          </div>

          {/* Myth 5 - AC vs Windows */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-898">
            <div className="flex items-start mb-4" data-id="element-899">
              <Wind className="text-[#B1050B] mr-3 flex-shrink-0 mt-1" size={28} data-id="element-900" />
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-901">
                5) "Does it really use less fuel to drive with the air
                conditioning on than to drive with the windows open?"
              </h2>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-4" data-id="element-902">
              <p className="text-gray-800 font-semibold mb-2" data-id="element-903">⚠ IT DEPENDS</p>
              <p className="text-gray-700 mb-4" data-id="element-904">
                There's a grain of truth in this. Opening the windows at freeway
                speeds increases the aerodynamic drag measurably. On the other
                hand, the air conditioning system consumes several horsepower.
              </p>
            </div>

            <div className="space-y-4 text-gray-700" data-id="element-905">
              <p data-id="element-906">
                <strong data-id="element-907">Our take:</strong> You can't really change the
                aerodynamics of a huge SUV or pickup much, so if it's not
                terribly warm, you probably are better off with the windows
                open. A Prius or some other low-drag-coefficient car will suffer
                more proportionally, so you might be better off with the a/c
                running on the highway.
              </p>
              <p data-id="element-908">
                If you must use the a/c, open the windows and use the fan to
                sweep away most of the hot air before you thumb the a/c switch.
                The recirculate setting is the most efficient way to keep hot
                air out and cool air in, at least once the cabin cools off.
              </p>
            </div>
          </div>

          {/* Myth 6 - Tire Tread */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-909">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-910">
              6) "Can you save a few bucks by using older tires with less tread
              during the summer, when snow tires are unnecessary?"
            </h2>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-4" data-id="element-911">
              <p className="text-gray-800 font-semibold mb-2" data-id="element-912">
                ✗ DANGEROUS MYTH
              </p>
              <p className="text-gray-700" data-id="element-913">
                The less tread on a tire, the more poorly it will perform on a
                wet road. If you're willing to stay home on rainy days, or
                simply park your car by the side of the road until it dries off,
                you'll be fine.
              </p>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-6" data-id="element-914">
              <p className="text-gray-800 font-semibold mb-2" data-id="element-915">
                ✓ SAFE PRACTICE:
              </p>
              <p className="text-gray-700" data-id="element-916">
                It's time to start thinking about new tires at{' '}
                <strong data-id="element-917">4/32" tread</strong>, and they're an absolute necessity
                at <strong data-id="element-918">2/32"</strong>, which is when the wear bars start to
                show. 4/32" is an absolute minimum tread depth for inclement
                weather.
              </p>
            </div>
          </div>

          {/* Myth 7 - Lightning */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-919">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-920">
              7) "Is it a bad idea to hide from a storm in a car because the
              metal frame will attract lightning?"
            </h2>

            <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-4" data-id="element-921">
              <p className="text-gray-800 font-semibold mb-2" data-id="element-922">
                ✓ CARS ARE SAFE (Mostly)
              </p>
              <p className="text-gray-700" data-id="element-923">
                Not really. At least not unless your car is the tallest object
                in the area. The car's metal bodywork should act as a Faraday
                cage, safely conducting the lightning to the ground around
                you--that is if you're not touching anything inside the car.
                (Corvette owners may fare differently).
              </p>
            </div>

            <div className="space-y-4 text-gray-700" data-id="element-924">
              <p data-id="element-925">
                Also, it should be safe to use your cellphone inside the car--as
                long as it's not connected to anything conductive, like a car
                charger.
              </p>
              <p data-id="element-926">
                Pulling up to a pay phone (remember pay phones?) during an
                electrical storm might not be a good idea. The lightning bolt's
                megavoltage could strike a nearby telephone pole, run down the
                wiring and wind up going straight into your ear. Ouch. What's
                that funny smell?
              </p>
            </div>
          </div>

          {/* Source */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-927">
            <p className="text-sm text-gray-600 italic text-center" data-id="element-928">
              Source: popularmechanics.com
            </p>
          </div>

          {/* Summer Maintenance Tips */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-929">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-930">
              Summer Maintenance Checklist
            </h2>

            <div className="space-y-3" data-id="element-931">
              <div className="flex items-start" data-id="element-932">
                <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-933" />
                <span className="text-gray-700" data-id="element-934">
                  Check tire pressures (when cold)
                </span>
              </div>
              <div className="flex items-start" data-id="element-935">
                <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-936" />
                <span className="text-gray-700" data-id="element-937">Inspect all fluid levels</span>
              </div>
              <div className="flex items-start" data-id="element-938">
                <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-939" />
                <span className="text-gray-700" data-id="element-940">Test cooling system</span>
              </div>
              <div className="flex items-start" data-id="element-941">
                <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-942" />
                <span className="text-gray-700" data-id="element-943">
                  Check hoses and belts for wear
                </span>
              </div>
              <div className="flex items-start" data-id="element-944">
                <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-945" />
                <span className="text-gray-700" data-id="element-946">Test battery condition</span>
              </div>
              <div className="flex items-start" data-id="element-947">
                <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-948" />
                <span className="text-gray-700" data-id="element-949">
                  Inspect air conditioning system
                </span>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-950">
            <h2 className="text-3xl font-bold mb-4" data-id="element-951">
              Get Your Summer Vehicle Checkup
            </h2>
            <p className="text-xl mb-6" data-id="element-952">
              Don't let myths put your vehicle at risk. Get professional summer
              maintenance today.
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-953">
              <Phone className="mr-3" size={24} data-id="element-954" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-955">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}